import React, { useEffect } from 'react';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import { Breadcrumbs, Link, Typography, Paper, Grid, Box, CircularProgress } from '@mui/material';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { DISABLED_CONTROL_TOOLTIP, PERMISSIONS } from '../../../../constants/variables';
import { ROUTES, getRouteWithParam } from '../../../../constants/routes';
import { CustomButton } from '../../../../components/CustomButton';
import { CustomInputWithMask } from '../../../../components/CustomInputWithMask';
import { CustomSelect } from '../../../../components/CustomSelect';
import { getUserById, updateUser } from '../../../../store/user/actions';
import { getCompanyBranches } from '../../../../store/company/actions';
import { getListRoles } from '../../../../store/role/actions';
import { visibleBySectionPermission } from '../../../../utils/visibleBySectionPermission';
import { setUsersPermissions } from '../../../../utils/setUsersPermissions';

export const EditProfileTeamPerson = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const pathname = location.pathname;
  const isCameFromTeamPage = !!location.state?.from?.match(ROUTES.TEAM);
  const id = pathname.substr(pathname.lastIndexOf('/') + 1);
  const history = useHistory();

  const userState = useSelector((state) => state.user);
  const { info: userInfo, fetching } = userState;

  const roleListState = useSelector((state) => state.roleList);
  const { items: roleOptions, fetching: fetchingRoleOptions } = roleListState;

  const companyBranchesState = useSelector((state) => state.company);
  const {
    companyInfo,
    branches: branchesOptions,
    fetching: fetchingBranchesOptions
  } = companyBranchesState;

  const { userPermissions, currentUsersPermissions } = useSelector((state) => state.userPermissions);
  const permissions = setUsersPermissions(userPermissions, currentUsersPermissions?.permissions);
  const title = 'Редактировать профиль сотрудника';

  const isNotAllowedToUpdateRole = !visibleBySectionPermission(permissions, PERMISSIONS.USER.UpdateUserRoles.value);
  const isNotAllowedToUpdateUsers = !visibleBySectionPermission(permissions, PERMISSIONS.USER.UpdateUsers.value);
  const isPageVisible =
    visibleBySectionPermission(permissions, [PERMISSIONS.USER.ListUsers.value, PERMISSIONS.USER.CreateUsers.value,], 'or') &&
    visibleBySectionPermission(permissions, PERMISSIONS.USER.UpdateUsers.value);

  useEffect(() => {
    if (isNotAllowedToUpdateUsers) {
      history.push(getRouteWithParam(ROUTES.TEAM_PROFILE_PERSON, ':id', id));
    } // eslint-disable-next-line
  }, [isNotAllowedToUpdateUsers]);

  useEffect(() => {
    dispatch(getUserById(id));
    dispatch(getListRoles());
    dispatch(getCompanyBranches());
  }, [dispatch, id]);

  const { handleSubmit, values, handleChange, handleBlur, setFieldValue } = useFormik({
    initialValues: {
      phone: userInfo.phone,
      role: roleOptions.find((role) => role.id === Number(userInfo.role?.id)),
      branch: { id: userInfo?.branch?.id, label: userInfo?.branch?.name },
      department: userInfo?.department?.id && {
        id: userInfo?.department?.id,
        label: userInfo?.department?.name
      },
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: (values) => {
      const isDataNotChanged = userInfo.phone === values.phone && userInfo.branch.id === values.branch?.id && userInfo.department?.id === values.department?.id;
      let data = null;

      if (!isDataNotChanged) {
        data = {
          firstName: userInfo.firstName,
          middleName: userInfo.middleName,
          lastName: userInfo.lastName,
          company_id: companyInfo.id,
          phone: values.phone,
          branch_id: values.branch?.id,
        };

        if (currentBranch.departments?.length && values.department?.id) {
          data.department_id = values.department?.id;
        }
      }

      dispatch(updateUser(id, data, !isNotAllowedToUpdateRole && values.role?.id !== Number(userInfo.role?.id) ? { roleId: values.role?.id } : null));

      setTimeout(() => {
        isCameFromTeamPage ?
          history.push(ROUTES.TEAM) :
          history.push(getRouteWithParam(ROUTES.TEAM_PROFILE_PERSON, ':id', id));
      }, 0);
    }
  });

  let currentBranch = branchesOptions?.find((branch) => branch.id === values.branch?.id);

  const branchChanged = (event) => {
    handleChange(event);

    currentBranch = branchesOptions?.find((branch) => branch.label === event.target.innerText);

    if (
      !currentBranch.department?.length ||
      !currentBranch.department.find((dep) => dep === values.department.label)
    ) {
      setFieldValue('department', null);
    }
  };

  if (!isPageVisible) {
    return <></>;
  }

  return (
    <>
      {fetching || !Object.keys(userInfo) ? (
        <Box
          sx={{
            marginTop: '150px',
            textAlign: 'center'
          }}>
          <CircularProgress data-testid="CircularProgress" />
        </Box>
      ) : (
        <>
          <Typography mb="24px" variant="h1" sx={{ color: 'text.primary' }} component="h1">
            {title}
          </Typography>
          <Breadcrumbs mb="24px" aria-label="breadcrumb">
            <Link
              component={RouterLink}
              underline="hover"
              color="inherit"
              to={getRouteWithParam(ROUTES.TEAM_PROFILE_PERSON, ':id', id)}>
              {userInfo.firstName} {userInfo.lastName}
            </Link>
            <Typography color="text.secondary">{title}</Typography>
          </Breadcrumbs>
          <Paper
            sx={{
              padding: '24px'
            }}>
            <form onSubmit={handleSubmit}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginBottom: '24px'
                }}>
                <Grid container spacing={2} mb="24px">
                  <Grid item>
                    <CustomInputWithMask
                      name="phone"
                      value={values.phone}
                      placeholder="Номер телефона"
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ width: 496 }}
                      size="small"
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item>
                    <CustomSelect
                      name="role"
                      value={values.role}
                      placeholder="Роль"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      variant="outlined"
                      sx={{ width: 240 }}
                      size="small"
                      options={roleOptions}
                      loading={fetchingRoleOptions}
                      setFieldValue={setFieldValue}
                      disabled={isNotAllowedToUpdateRole}
                      tooltip={isNotAllowedToUpdateRole ? DISABLED_CONTROL_TOOLTIP : null}
                    />
                  </Grid>
                  <Grid item>
                    <CustomSelect
                      name="branch"
                      value={values.branch}
                      placeholder="Филиал"
                      onChange={branchChanged}
                      variant="outlined"
                      sx={{ width: 240 }}
                      size="small"
                      options={branchesOptions}
                      loading={fetchingBranchesOptions}
                      setFieldValue={setFieldValue}
                    />
                  </Grid>
                  <Grid item>
                    <CustomSelect
                      name="department"
                      placeholder="Департамент"
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ width: 240 }}
                      size="small"
                      value={values.department}
                      options={currentBranch?.departments}
                      setFieldValue={setFieldValue}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                <Box>
                  <CustomButton
                    component={RouterLink}
                    to={isCameFromTeamPage ? ROUTES.TEAM : ROUTES.TEAM_PROFILE_PERSON}
                    color="secondary"
                    variant="contained"
                    sx={{ marginRight: '16px' }}>
                    Отмена
                  </CustomButton>
                </Box>
                <CustomButton type="submit" variant="contained">
                  Сохранить
                </CustomButton>
              </Box>
            </form>
          </Paper>
        </>
      )}
    </>
  );
};
